/**
 * Base API service for making HTTP requests
 * Provides centralized configuration, error handling, and request/response interceptors
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios'

// Base API configuration
const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:5001/api'
const REQUEST_TIMEOUT = 60000 // 60 seconds for chat messages

// API response types
export interface ApiResponse<T = any> {
  data?: T
  message?: string
  error?: string
  details?: any
}

export interface ApiError {
  message: string
  status: number
  details?: any
}

// Create axios instance with base configuration
const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: API_BASE_URL,
    timeout: REQUEST_TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  // Request interceptor to add auth token
  client.interceptors.request.use(
    (config) => {
      // Get token from localStorage (will be set after successful login)
      const token = tokenManager.getToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  // Response interceptor for centralized error handling
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      return response
    },
    (error: AxiosError) => {
      const apiError: ApiError = {
        message: 'An unexpected error occurred',
        status: 500,
      }

      if (error.response) {
        // Server responded with error status
        const { status, data } = error.response
        apiError.status = status
        apiError.message = (data as any)?.error || (data as any)?.message || `HTTP ${status} Error`
        apiError.details = (data as any)?.details
      } else if (error.request) {
        // Request made but no response received
        apiError.message = 'Network error - please check your connection'
        apiError.status = 0
      } else {
        // Something else happened
        apiError.message = error.message || 'Request failed'
      }

      return Promise.reject(apiError)
    }
  )

  return client
}

// Create the API client instance
export const apiClient = createApiClient()

// Generic API methods
export const api = {
  get: async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    const response = await apiClient.get<T>(url, config)
    return response.data
  },

  post: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    const response = await apiClient.post<T>(url, data, config)
    return response.data
  },

  put: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    const response = await apiClient.put<T>(url, data, config)
    return response.data
  },

  patch: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    const response = await apiClient.patch<T>(url, data, config)
    return response.data
  },

  delete: async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    const response = await apiClient.delete<T>(url, config)
    return response.data
  },
}

// Token management utilities
export const tokenManager = {
  getToken: (): string | null => {
    return localStorage.getItem('authToken')
  },

  setToken: (token: string): void => {
    localStorage.setItem('authToken', token)
  },

  removeToken: (): void => {
    localStorage.removeItem('authToken')
  },

  isTokenValid: (): boolean => {
    const token = tokenManager.getToken()
    if (!token) return false

    try {
      // Basic JWT structure validation
      const parts = token.split('.')
      if (parts.length !== 3) return false

      // Decode payload to check expiration
      const payload = JSON.parse(atob(parts[1]))
      const currentTime = Math.floor(Date.now() / 1000)
      
      return payload.exp > currentTime
    } catch {
      return false
    }
  },
}

